import { useCallback, useRef } from 'react';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type EligibilityEventCacheValue,
	cjnEventEligibilityCache,
	type EligibilityEventCacheKey,
} from '../caching/index.tsx';

export const useMsrAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const eventIsFiredRef = useRef(false);

	/**
	 * Fire customer feature exposed event only once
	 */
	const fireMsrFeatureExposedEventOnce = useCallback(
		({ cohort }: { cohort: string }): void => {
			if (eventIsFiredRef.current) {
				return;
			}
			if (cohort === 'not-enrolled') {
				return;
			}
			fireTrackAnalytics(createAnalyticsEvent({}), 'msrCjnExperiment exposed', {
				cohort,
			});
			eventIsFiredRef.current = true;
		},
		[createAnalyticsEvent],
	);

	/**
	 * Fire event only in case the event value has not been cached or cached value is different
	 * to prevent excessive number of analytics calls.
	 */
	const fireOnlyNewEvent = useCallback(
		async ({
			value,
			eventName,
			key,
		}: {
			value: EligibilityEventCacheValue;
			eventName: string;
			key: EligibilityEventCacheKey;
		}): Promise<void> => {
			try {
				const cachedValue = await cjnEventEligibilityCache.get(key);
				if (
					!cachedValue ||
					(cachedValue &&
						cachedValue?.isEligible !== value.isEligible &&
						cachedValue?.reason !== value.reason)
				) {
					fireTrackAnalytics(createAnalyticsEvent({}), eventName, {
						...value,
						key,
					});
					await cjnEventEligibilityCache.set(key, value);
				}
			} catch (e) {
				// cache get fail, do nothing
			}
		},
		[createAnalyticsEvent],
	);

	return {
		fireMsrFeatureExposedEventOnce,
		fireOnlyNewEvent,
	};
};
